import React from 'react';
import {
  GlobeAltIcon,
  TruckIcon,
  BuildingOffice2Icon,
  ClockIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  CubeIcon,
  WrenchScrewdriverIcon,
  ComputerDesktopIcon,
  HandRaisedIcon,
  BoltIcon,
  DocumentCheckIcon,
} from '@heroicons/react/24/outline';
import { SolutionPageTemplate } from '../../components/page-templates/MarketPageTemplates';

const painPoints = [
  {
    icon: GlobeAltIcon,
    title: 'Global Complexity',
    description: 'You manage multi-country rollouts, each with different rules and risks',
  },
  {
    icon: ClockIcon,
    title: 'Delivery Pressure',
    description: 'Your projects are tied to fixed timelines and client SLAs',
  },
  {
    icon: UserGroupIcon,
    title: 'Vendor Sprawl',
    description: 'You coordinate too many local partners across regions',
  },
  {
    icon: ExclamationTriangleIcon,
    title: 'Last-Mile Risk',
    description: 'On-site failures reflect directly on you, not your suppliers',
  },
];

const solutions = [
  {
    icon: TruckIcon,
    title: 'Freight Forwarding',
    points: [
      'Reliable transit with consolidated shipments',
      'Reduced customs delays',
      'Predictable timelines',
    ],
  },
  {
    icon: DocumentCheckIcon,
    title: 'IOR & EOR',
    points: [
      'Legal importation in regulated markets',
      'Duties and taxes management',
      'Faster clearance and market entry without entity setup',
    ],
  },
  {
    icon: BuildingOffice2Icon,
    title: 'International Warehousing',
    points: [
      'Regional inventory positioning',
      'Centralised stock visibility',
      'Faster project activation and cross-border redistribution',
    ],
  },
  {
    icon: CubeIcon,
    title: 'Last-Mile Delivery',
    points: [
      'Coordinated site deliveries',
      'Scheduled drop-offs with reduced failed deliveries',
      'Final handover visibility',
    ],
  },
  {
    icon: ComputerDesktopIcon,
    title: 'Staging & Configuration',
    points: [
      'Device imaging, config, and software pre-load',
      'Kitting and labelling',
      'Pre-validated hardware delivered installation-ready',
    ],
  },
  {
    icon: HandRaisedIcon,
    title: 'Remote Hands & On-Site Support',
    points: [
      'Rack & stack and smart-hands in data centres',
      'Scheduled site coordination',
      'Multi-region coverage',
    ],
  },
];

const whyChooseCards = [
  {
    icon: BoltIcon,
    title: 'Faster Delivery Cycles',
    description: 'Inventory positioned in major trade corridors reduces lead times and accelerates project timelines.',
    accent: 'bg-blue-500',
  },
  {
    icon: ShieldCheckIcon,
    title: 'Stronger SLA & Customer Retention',
    description: 'Structured forward and reverse flows protect uptime and strengthen your service commitments.',
    accent: 'bg-cyan-500',
  },
  {
    icon: WrenchScrewdriverIcon,
    title: 'Operational Focus on High-Value Work',
    description: 'Your teams focus on integration while we manage execution across regions.',
    accent: 'bg-indigo-500',
  },
];

const SystemIntegratorsMsps: React.FC = () => (
  <SolutionPageTemplate
    eyebrow="System Integrators & MSPs"
    title="Scale Internationally With One Trusted Logistics Partner"
    highlight="Trusted Logistics Partner"
    description="TrueLog acts as your execution backbone, managing regional and cross-border logistics so you can focus on implementation, service delivery, and customer growth."
    heroIcon={WrenchScrewdriverIcon}
    heroAccentIcon={GlobeAltIcon}
    problemHeading="Execution complexity slows service revenue"
    problemDescription="Service-led technology partners need predictable logistics across regions without drowning in vendor coordination."
    painPoints={painPoints}
    solutionsIntro="TrueLog gives you one execution layer behind every delivery, installation, and reverse workflow."
    solutions={solutions}
    whyChooseCards={whyChooseCards}
    ctaTitle="Ready to scale your service delivery globally?"
    ctaDescription="Let TrueLog handle the execution layer behind your projects so your team can stay focused on integration, uptime, and customer growth."
  />
);

export default SystemIntegratorsMsps;
